import { Outlet, createRootRoute, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { measureOpenAIPageView } from "@/lib/openaiPixel";


import appCss from "../styles.css?url";
import { NotFoundPage } from "../components/NotFoundPage";
import { RouteProgressBar } from "@/components/RouteProgressBar";

import {
  ORGANIZATION_SCHEMA,
  LOCAL_BUSINESS_SCHEMA,
  PERSON_SCHEMA,
  buildSpeakableSchema,
  buildOfferingServiceJsonLd,
  BRANCH_LOCAL_BUSINESSES,
} from "@/lib/entitySchema";
import { CLIENT_REVIEWS, aggregateRatingFromReviews } from "@/lib/clientReviews";
import { logoImageObject, chrisImageObject } from "@/lib/brandImageSchema";

// Sitewide AggregateRating — computed once at module load so every page
// emits LocalBusiness with star ratings. Eligible for Google rich results
// (★ snippet) on every URL where LocalBusiness is the primary entity.
const SITEWIDE_AGG = aggregateRatingFromReviews(CLIENT_REVIEWS);
// Build-time stamp baked into Organization/LocalBusiness JSON-LD as
// `dateModified`. Bump on each deploy so freshness signals propagate to
// search engines and AI crawlers without per-page wiring.
const SITE_DATE_MODIFIED = new Date().toISOString().split("T")[0];
const LOCAL_BUSINESS_WITH_RATING = {
  ...LOCAL_BUSINESS_SCHEMA,
  dateModified: SITE_DATE_MODIFIED,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: SITEWIDE_AGG.ratingValue,
    reviewCount: SITEWIDE_AGG.ratingCount,
    bestRating: 5,
    worstRating: 1,
  },
};
const ORGANIZATION_WITH_DATE = {
  ...ORGANIZATION_SCHEMA,
  dateModified: SITE_DATE_MODIFIED,
};
// Sitewide WebSite + Speakable JSON-LD — applies speakable hints to every
// page automatically (selectors target H1, [data-speakable], and the first
// paragraph in <main>). Voice assistants and AI engines use this to extract
// the spoken summary of any page.
const SITEWIDE_SPEAKABLE = buildSpeakableSchema("https://coloradowebimpressions.com/");
// Standalone Service nodes — one per first-class offering. Each shares an @id
// with the hub-page Service JSON-LD so Google entity-merges them rather than
// treating them as duplicates.
const OFFERING_SERVICES = buildOfferingServiceJsonLd();

export const Route = createRootRoute({
  // Edge cache hints — Cloudflare honors `s-maxage` for shared caches and
  // serves stale content while revalidating in the background. Browsers
  // revalidate on every navigation (`max-age=0`) so users always see fresh
  // copy after a deploy, while repeat hits within the hour come from the
  // edge in <100ms (vs the current 1.1s TTFB). POST routes (form submits,
  // server functions) are unaffected because Cache-Control only applies to
  // GET responses.
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=86400, stale-while-revalidate=604800",
    "CDN-Cache-Control": "public, s-maxage=86400, stale-while-revalidate=604800",
    Vary: "Accept-Encoding",
  }),
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Colorado Web Impressions | CO Web Design & SEO" },
      {
        name: "description",
        content:
          "Colorado web design & SEO that drives leads, calls and booked jobs for local businesses. Serving Colorado Springs, Denver & beyond.",
      },
      { name: "author", content: "Colorado Web Impressions" },
      { name: "publisher", content: "Colorado Web Impressions" },
      { name: "google-site-verification", content: "rr0DAnTdi5QYTiM26jOIOAfOlqZsX06lrkAS6N-gvMs" },
      // Sitewide default robots directive — customer-facing pages should be
      // indexable by default. Internal/conversion pages (e.g. /thank-you,
      // /launch-checklist, /free-audit, /content-checklist) override this
      // with their own `name: "robots"` meta, which dedupes by name.
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      // Site-wide defaults — child routes override title/description/og/twitter
      // text via their own head() blocks. These exist so every crawl has at
      // least one of each tag even on routes that forget to set their own.
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Colorado Web Impressions" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: "Colorado Web Impressions | CO Web Design & SEO" },
      { name: "twitter:title", content: "Colorado Web Impressions | CO Web Design & SEO" },
      { property: "og:description", content: "Colorado web design, SEO & PPC. We help local businesses rank on Google, win in Maps, and turn sites into lead machines." },
      { name: "twitter:description", content: "Colorado web design, SEO & PPC. We help local businesses rank on Google, win in Maps, and turn sites into lead machines." },
      // PWA / mobile browser theming
      { name: "theme-color", content: "#d4af37" },
      { name: "application-name", content: "Colorado Web Impressions" },
      { name: "apple-mobile-web-app-title", content: "Colorado Web Impressions" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      // Single-language site — declare en-US explicitly so crawlers don't
      // guess. Structured so additional locales can be added later by simply
      // appending more hreflang link tags here or per-route.
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Favicons — Colorado Web Impressions logo mark
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      // Web App Manifest (PWA signals + mobile theming)
      { rel: "manifest", href: "/manifest.json" },
      // hreflang — single-language US English. Listed once at the root so it
      // applies site-wide; per-route canonical tags handle the URL specifics.
      // To add Spanish later: import hreflangLinksFor from "@/lib/hreflang"
      // and emit per-route alternates instead of these root-level ones.
      { rel: "alternate", hrefLang: "en-us", href: "https://coloradowebimpressions.com/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://coloradowebimpressions.com/" },
      // Performance — preconnect for critical third-party origins, plus
      // dns-prefetch fallbacks for older browsers.
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      { rel: "dns-prefetch", href: "https://fonts.gstatic.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google.com" },
      { rel: "dns-prefetch", href: "https://elfsightcdn.com" },
      { rel: "preconnect", href: "https://elfsightcdn.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://acsbapp.com" },
      { rel: "preconnect", href: "https://acsbapp.com", crossOrigin: "anonymous" },
      // Meta Pixel — preconnect for faster first event delivery
      { rel: "dns-prefetch", href: "https://connect.facebook.net" },
      { rel: "preconnect", href: "https://connect.facebook.net", crossOrigin: "anonymous" },
      // OpenAI Ads pixel
      { rel: "dns-prefetch", href: "https://bzrcdn.openai.com" },
      { rel: "preconnect", href: "https://bzrcdn.openai.com", crossOrigin: "anonymous" },
      // Non-render-blocking Google Fonts. Preload starts the download early;
      // a tiny inline script (in scripts[] below) injects the real stylesheet
      // after first paint so it never blocks FCP. Trimmed to weights actually
      // used in the design (400/600/700/800) — drops ~30KB vs 6 weights.
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap",
      },
      // RSS feed for /learn — auto-discovered by feed readers, AI training
      // crawlers, and Google for content freshness signals.
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Colorado Web Impressions — Learn",
        href: "https://coloradowebimpressions.com/rss.xml",
      },
    ],
    scripts: [
      // Analytics/marketing API stubs — initialize the global queues
      // synchronously so any gtag()/fbq()/clarity() calls fired during page
      // render (e.g. ContactForm Lead, thank-you page CompleteRegistration)
      // are buffered and replayed once the real libraries load. This lets us
      // safely defer ALL network requests behind first-interaction/idle
      // without losing conversion events.
      // HOSTNAME GUARD: marketing pixel `config`/`init`/PageView calls only
      // fire on production hostnames. Stubs are always defined so any code
      // calling gtag/fbq/clarity doesn't crash on preview.
      {
        children:
          "window.dataLayer=window.dataLayer||[];window.gtag=function(){dataLayer.push(arguments);};" +
          "!function(f,b,e,v,n){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];}(window);" +
          "window.clarity=window.clarity||function(){(clarity.q=clarity.q||[]).push(arguments)};" +
          "window.oaiq=window.oaiq||function(){(window.oaiq.q=window.oaiq.q||[]).push(arguments);};" +
          "(function(){var h=location.hostname;if(h==='coloradowebimpressions.com'||h==='www.coloradowebimpressions.com'){" +
          "gtag('js',new Date());gtag('config','G-SCRR7ZBM2W');gtag('config','AW-10954529873');gtag('config','GT-K5Q772P');" +
          "fbq('init','2461853347586995');fbq('track','PageView');" +
          "oaiq('init',{pixelId:'PQq4ZSkkBkHkFmauYHH3WM'});" +
          "}})();",
      },
      // accessiBe ADA accessibility widget — loads immediately on every
      // hostname (not deferred) so the accessibility toolbar is always
      // available the moment the page renders.
      {
        children:
          "(function(){var s=document.createElement('script');s.src='https://acsbapp.com/apps/app/dist/js/app.js';s.async=true;s.onload=function(){if(window.acsbJS){window.acsbJS.init();}};(document.head||document.body).appendChild(s);})();",
      },
      // Unified deferred 3rd-party loader. Tracking pixels (GTM, GA4, Meta,
      // Clarity) ONLY load on production hostnames. Lovable preview /
      // sandbox URLs (*.lovableproject.com, *.lovable.app) get fonts +
      // Elfsight only so MNTN, Google Ads, and Meta don't attribute
      // conversions to preview URLs while we test the site.
      {
        children:
          "(function(){var loaded=false;var h=location.hostname;var prod=(h==='coloradowebimpressions.com'||h==='www.coloradowebimpressions.com');function injStyle(h){var l=document.createElement('link');l.rel='stylesheet';l.href=h;document.head.appendChild(l);}function injScript(s,a){var el=document.createElement('script');el.src=s;el.async=true;if(a){Object.keys(a).forEach(function(k){el.setAttribute(k,a[k]);});}document.head.appendChild(el);}function load(){if(loaded)return;loaded=true;" +
          "injStyle('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');" +
          "injScript('https://elfsightcdn.com/platform.js');" +
          "if(prod){" +
          "injScript('https://www.googletagmanager.com/gtm.js?id=GTM-P4WJCHP4');" +
          "injScript('https://www.googletagmanager.com/gtag/js?id=G-SCRR7ZBM2W');" +
          "injScript('https://connect.facebook.net/en_US/fbevents.js');" +
          "injScript('https://www.clarity.ms/tag/ko17tt56fs');" +
          "injScript('https://bzrcdn.openai.com/sdk/oaiq.min.js');" +
          "}" +
          "['scroll','mousemove','touchstart','keydown','click'].forEach(function(e){window.removeEventListener(e,load);});}" +
          "['scroll','mousemove','touchstart','keydown','click'].forEach(function(e){window.addEventListener(e,load,{passive:true,once:true});});" +
          "if('requestIdleCallback' in window){requestIdleCallback(load,{timeout:1500});}else{setTimeout(load,1500);}})();",
      },
      // LeadConnector live chat — production hostnames only.
      {
        children:
          "(function(){var h=location.hostname;if(h!=='coloradowebimpressions.com'&&h!=='www.coloradowebimpressions.com')return;function load(){var s=document.createElement('script');s.src='https://beta.leadconnectorhq.com/loader.js';s.async=true;s.setAttribute('data-resources-url','https://beta.leadconnectorhq.com/chat-widget/loader.js');s.setAttribute('data-widget-id','69eaa9efc59bb88519d75796');document.body.appendChild(s);}if(document.readyState==='complete'){setTimeout(load,5000);}else{window.addEventListener('load',function(){setTimeout(load,5000);});}})();",
      },
      // MNTN tracking pixel (shaid 66855) — production hostnames only.
      // Deferred to first interaction / idle so it doesn't impact LCP.
      {
        children:
          "(function(){var h=location.hostname;if(h!=='coloradowebimpressions.com'&&h!=='www.coloradowebimpressions.com')return;var fired=false;function fire(){if(fired)return;fired=true;try{var t,i,o='66855';try{t=top.document.referrer!==''?encodeURIComponent(top.document.referrer.substring(0,2048)):''}catch(e){t=document.referrer!==null?document.referrer.toString().substring(0,2048):''}try{i=parent.location.href!==''?encodeURIComponent(parent.location.href.toString().substring(0,2048)):''}catch(e){i=''}var l=Math.floor(Math.random()*1e17);var s=document.createElement('script');s.type='text/javascript';s.async=true;s.src='https://dx.mountain.com/spx?shaid='+o+'&tdr='+t+'&plh='+i+'&cb='+l;document.head.appendChild(s);}catch(e){}}['scroll','mousemove','touchstart','keydown','click'].forEach(function(e){window.addEventListener(e,fire,{passive:true,once:true});});if('requestIdleCallback' in window){requestIdleCallback(fire,{timeout:1500});}else{setTimeout(fire,1500);}})();",
      },
      // Sitewide entity graph: Organization + LocalBusiness + Person.
      // All three share linked @id values (see src/lib/entitySchema.ts) so
      // Google's Knowledge Graph and AI engines (ChatGPT, Perplexity, Gemini)
      // can resolve them as one connected entity. Per-page schemas (Article,
      // Service, Review, etc.) reference these @ids to attribute authorship
      // and provider info — a key E-E-A-T signal.
      {
        type: "application/ld+json",
        children: JSON.stringify(ORGANIZATION_WITH_DATE),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(LOCAL_BUSINESS_WITH_RATING),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(PERSON_SCHEMA),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(SITEWIDE_SPEAKABLE),
      },
      // Geo-tagged ImageObject schemas for primary brand assets so image
      // search and AI crawlers attribute the logo + owner photo to the
      // Colorado Springs (Woodmen Rd) office.
      {
        type: "application/ld+json",
        children: JSON.stringify(
          logoImageObject(
            "/assets/colorado-web-impressions-logo.png",
            "Colorado Web Impressions logo — Colorado Springs web design and SEO agency"
          )
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          chrisImageObject(
            "/assets/chris-heidlebaugh-colorado-web-designer.png",
            "Chris Heidlebaugh, owner of Colorado Web Impressions — Colorado Springs web designer and SEO expert"
          )
        ),
      },
      // Individual Service nodes — one per offering, each with its own @id,
      // url, areaServed, provider, and a name matching the hub-page H1.
      ...OFFERING_SERVICES.map((service) => ({
        type: "application/ld+json" as const,
        children: JSON.stringify(service),
      })),
      // Per-metro LocalBusiness branch nodes — Denver, Pueblo, Castle Rock,
      // Parker, Monument. Each links back to the HQ LocalBusiness via
      // branchOf so Google treats them as one entity with multiple service areas.
      ...BRANCH_LOCAL_BUSINESSES.map((branch) => ({
        type: "application/ld+json" as const,
        children: JSON.stringify(branch),
      })),
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundPage,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {/* GTM + Meta Pixel noscript fallbacks — production hostnames only.
            Rendered client-side after hydration so preview SSR HTML never
            ships the tracking iframe/img. */}
        <ProdNoscriptPixels />

        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  // Track the last non-contact page so the contact form can record where the
  // visitor actually came from (the route they clicked the CTA on).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (pathname && pathname !== "/contact") {
      try { sessionStorage.setItem("cwi_referring_page", pathname); } catch {}
    }
  }, [pathname]);

  // OpenAI Ads page_view — initial load + every client-side navigation.
  useEffect(() => {
    measureOpenAIPageView(pathname);
  }, [pathname]);


  return (
    <>
      <RouteProgressBar />
      <Outlet />
    </>
  );
}

const PROD_HOSTS = new Set(["coloradowebimpressions.com", "www.coloradowebimpressions.com"]);

function ProdNoscriptPixels() {
  // Render only on production hostnames, client-side post-hydration.
  // SSR returns null so preview HTML never includes the pixel fallbacks.
  if (typeof window === "undefined") return null;
  if (!PROD_HOSTS.has(window.location.hostname)) return null;
  return (
    <>
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-P4WJCHP4"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        />
      </noscript>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=2461853347586995&ev=PageView&noscript=1"
          alt=""
        />
      </noscript>
    </>
  );
}
